/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { AddressValidationModel } from "@eisinc/App/Services/AddressValidationWithAvalaraService";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface AddressSuggestionProps {
    addressSuggestionModalIsOpen: boolean;
    addressSuggestionModalCloseHandler: () => void;
    selectedAddressSuggestionId: string;
    saveSelectedAddressSuggestion: (id: string | undefined) => void;
    addressSuggestionList: AddressValidationModel[];
    setSelectedAddressSuggestionId: (id: string) => void;
    addressValidationErrorModalIsOpen: boolean;
    addressValidationErrorModalCloseHandler: () => void;
    addressValidationError: string;
    addressSuggestionsSkip: () => void;
    title?: string;
    addressSuggestionsSkipButton?: string;
    forcedAvalara?: boolean;
    address: AddressValidationModel;
}

export interface AddressSuggestionStyles {
    outerContainer?: InjectableCss;
    addressSuggestionModal?: ModalPresentationProps;
    addressValidationErrorModal?: ModalPresentationProps;
    addressSuggestionsWrapper: InjectableCss;
    addressValidationErrorsWrapper: InjectableCss;
    addressSuggestionsSkipButton: ButtonPresentationProps;
    addressSuggestionsSaveButton: ButtonPresentationProps;
    title?: TypographyPresentationProps;
    selectedAddress?: TypographyPresentationProps;
    selectedAddressWrapper?: InjectableCss;
    addressSuggestionsOriginButton?: ButtonPresentationProps;
}

export const styles: AddressSuggestionStyles = {
    outerContainer: {
        css: css`
            padding: 20px;
            align-items: center;
        `,
    },
    addressSuggestionModal: { sizeVariant: "small" },
    addressValidationErrorModal: { sizeVariant: "small" },

    addressSuggestionsWrapper: {
        css: css`
            margin-top: 30px !important;
            display: flex;
            justify-content: flex-end;
        `,
    },
    addressSuggestionsSkipButton: {
        variant: "secondary",
    },
    addressSuggestionsSaveButton: {
        css: css`
            margin: 1px 0;
            padding: 10px 25px;
            ${({ forcedAvalara }: any) =>
                !forcedAvalara
                    ? css`
                          width: calc(100% - 12px);
                      `
                    : css`
                          width: 100%;
                      `}
        `,
    },
    addressSuggestionsOriginButton: {
        variant: "secondary",
        css: css`
            margin: 1px 0;
            padding: 10px 25px;
            ${({ forcedAvalara }: any) =>
                !forcedAvalara
                    ? css`
                          width: calc(100% - 12px);
                      `
                    : css`
                          width: 100%;
                      `}
        `,
    },
    addressValidationErrorsWrapper: {
        css: css``,
    },
    title: {
        size: "16px",
        lineHeight: "20px",
        color: "text.main",
        css: css`
            margin-bottom: 28px;
            ${({ forcedAvalara }: any) => {
                if (!forcedAvalara) {
                    return css`
                        font-weight: 500;
                        margin-bottom: 16px;
                    `;
                }
            }}
        `,
    },
    selectedAddress: {
        size: "16px",
        lineHeight: "20px",
        color: "text.main",
        weight: 500,
        css: css`
            margin: 16px 0;
        `,
    },
    selectedAddressWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            margin-bottom: 16px;
        `,
    },
};

const AddressSuggestion: FC<AddressSuggestionProps> = ({
    addressSuggestionModalIsOpen,
    addressSuggestionModalCloseHandler,
    selectedAddressSuggestionId,
    saveSelectedAddressSuggestion,
    addressSuggestionList,
    setSelectedAddressSuggestionId,
    addressValidationError,
    addressValidationErrorModalIsOpen,
    addressValidationErrorModalCloseHandler,
    addressSuggestionsSkip,
    title,
    addressSuggestionsSkipButton,
    forcedAvalara,
    address,
}) => {
    const getAddressText = (address: AddressValidationModel) => {
        return `${address.addressLine1 || address.line1}, ${address.city}, ${address.state || address.region} ${
            address.postalCode
        }, ${address.country}`;
    };

    const setSelectedItem = (suggestion: AddressValidationModel) => {
        saveSelectedAddressSuggestion(suggestion.id);
    };
    return (
        <>
            <Modal
                headline={title ?? translate("Address Suggestions")}
                {...styles.addressSuggestionModal}
                isOpen={addressSuggestionModalIsOpen}
                handleClose={() => addressSuggestionModalCloseHandler()}
            >
                <Typography as="h2" {...styles.title} {...{ forcedAvalara }}>
                    {siteMessage("Custom_AddressSuggestionPopup_Header")}
                </Typography>
                {selectedAddressSuggestionId}
                <StyledWrapper {...styles.selectedAddressWrapper} {...{ forcedAvalara }}>
                    {addressSuggestionList.map(validatedAddress => {
                        const value = getAddressText(validatedAddress);
                        return (
                            <Button
                                {...styles.addressSuggestionsSaveButton}
                                key={(validatedAddress as any).id}
                                type="button"
                                onClick={() => setSelectedItem(validatedAddress)}
                                {...{ forcedAvalara }}
                            >
                                {value}
                            </Button>
                        );
                    })}
                </StyledWrapper>
                {!forcedAvalara && (
                    <>
                        <Typography as="h2" {...styles.selectedAddress} {...{ forcedAvalara }}>
                            {siteMessage("Custom_AddressSuggestionPopup_SelectedAddress")}
                        </Typography>
                        <StyledWrapper {...styles.selectedAddressWrapper}>
                            <Button
                                {...styles.addressSuggestionsOriginButton}
                                type="button"
                                onClick={addressSuggestionsSkip}
                                {...{ forcedAvalara }}
                            >
                                {getAddressText(address)}
                            </Button>
                        </StyledWrapper>
                    </>
                )}
                {forcedAvalara && (
                    <StyledWrapper {...styles.addressSuggestionsWrapper} className="modal-actions">
                        <Button {...styles.addressSuggestionsSkipButton} type="button" onClick={addressSuggestionsSkip}>
                            {addressSuggestionsSkipButton}
                        </Button>
                    </StyledWrapper>
                )}
            </Modal>
            <Modal
                headline={siteMessage("Custom_AddressSuggestion_Modal_Title")}
                {...styles.addressValidationErrorModal}
                isOpen={addressValidationErrorModalIsOpen}
                handleClose={addressValidationErrorModalCloseHandler}
            >
                <StyledWrapper {...styles.addressValidationErrorsWrapper}>
                    <Typography>{addressValidationError}</Typography>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default AddressSuggestion;
